import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, PageHeader } from '../components/ui';
import { Calendar, Plus, User, MoreHorizontal, Clock, LayoutGrid, CheckCircle2, CircleDashed, ArrowRightCircle, AlertCircle, PlayCircle } from 'lucide-react';

const COLUMNS = ['Backlog', 'To Do', 'In Progress', 'In Review', 'Done'];

const getStatusConfig = (status) => {
  switch(status) {
    case 'Backlog': return { color: 'var(--text-muted)', icon: CircleDashed, bg: 'var(--bg-hover)' };
    case 'To Do': return { color: '#60a5fa', icon: AlertCircle, bg: 'rgba(96, 165, 250, 0.1)' };
    case 'In Progress': return { color: '#f59e0b', icon: PlayCircle, bg: 'rgba(245, 158, 11, 0.1)' };
    case 'In Review': return { color: '#c084fc', icon: ArrowRightCircle, bg: 'rgba(192, 132, 252, 0.1)' };
    case 'Done': return { color: '#34d399', icon: CheckCircle2, bg: 'rgba(52, 211, 153, 0.1)' };
    default: return { color: 'var(--accent)', icon: CircleDashed, bg: 'var(--bg-hover)' };
  }
};

export default function Board() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [counts, setCounts] = useState({
    Backlog: 0,
    'To Do': 0,
    'In Progress': 0,
    'In Review': 0,
    Done: 0,
  });
  const [pages, setPages] = useState({
    Backlog: 1, 'To Do': 1, 'In Progress': 1, 'In Review': 1, Done: 1
  });
  const PAGE_SIZE = 5;
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchProjects() {
      try {
        const { data } = await projectsAPI.list();
        const projectsData = data.data || data || [];
        setProjects(Array.isArray(projectsData) ? projectsData : []);
        
        const statusCounts = { Backlog: 0, 'To Do': 0, 'In Progress': 0, 'In Review': 0, Done: 0 };
        (Array.isArray(projectsData) ? projectsData : []).forEach((p) => {
          const s = p.status;
          if (statusCounts.hasOwnProperty(s)) statusCounts[s] += 1;
        });
        setCounts(statusCounts);
      } catch (err) {
        console.error('Failed to load projects', err);
      } finally {
        setLoading(false);
      }
    }
    fetchProjects();
  }, []);

  if (loading) return (
    <div className="flex h-full items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-[var(--border-bright)] border-t-[var(--accent)] rounded-full animate-spin"></div>
        <p className="text-[var(--text-muted)] font-medium">Loading workspace...</p>
      </div>
    </div>
  );

  if (!user || user.role !== 'Admin') {
    return (
      <div className="p-12 text-center flex flex-col items-center justify-center h-full">
        <div className="w-20 h-20 rounded-full bg-[var(--bg-hover)] border-2 border-[var(--border-bright)] flex items-center justify-center mb-6">
          <User size={32} className="text-[var(--text-muted)]" />
        </div>
        <h2 className="text-2xl font-display font-bold text-[var(--text)] mb-3">Access Restricted</h2>
        <p className="text-[var(--text-muted)] max-w-md">The project board overview is restricted to administrative personnel. Please contact your workspace owner for access.</p>
      </div>
    );
  }

  const columnsData = COLUMNS.reduce((acc, col) => {
    acc[col] = projects.filter((p) => p.status === col);
    return acc;
  }, {});

  const handleProjectClick = (projectId) => {
    navigate(`/app/projects?project_id=${projectId}`);
  };

  return (
    <div className="flex flex-col h-full bg-[var(--bg)] text-[var(--text)] animate-in overflow-hidden" style={{ padding: '0 24px 24px 24px' }}>
      <PageHeader
        eyebrow="Project Pipeline"
        title="Active Board"
        subtitle="View and manage your project workflow across backlog, in progress, review, and done statuses."
        action={
          <button onClick={() => navigate('/app/projects')} className="flex items-center gap-2 px-6 py-2.5 bg-[var(--text)] text-[var(--bg)] font-bold rounded-[var(--radius-sm)] shadow-md hover:bg-white hover:-translate-y-0.5 transition-all duration-200">
            <Plus size={18} strokeWidth={3} />
            <span>New Project</span>
          </button>
        }
        style={{
          '--page-header-bg': 'linear-gradient(135deg, #0f172a, #1e293b)',
          '--page-header-text': '#fff',
          '--page-header-muted': 'rgba(255,255,255,0.78)',
        }}
      />

      <div className="pt-2 pb-6 flex flex-wrap justify-between items-center gap-4 mb-6">
        <div className="flex bg-[var(--bg-card)] border border-[var(--border-bright)] rounded-[var(--radius-sm)] overflow-hidden shadow-sm mr-2">
          <div className="px-4 py-2 bg-[var(--bg-hover)] border-r border-[var(--border)] text-sm font-semibold text-[var(--text-primary)]">
            Total Projects
          </div>
          <div className="px-5 py-2 text-sm font-bold text-[var(--accent)] bg-[var(--accent-surface)]">
            {projects.length}
          </div>
        </div>
      </div>

      {/* Kanban Board Columns - Grid Layout to prevent horizontal scroll */}
      <div className="flex-1 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-5 h-full">
          {COLUMNS.map((colName) => {
            const colProjects = columnsData[colName] || [];
            const config = getStatusConfig(colName);
            const StatusIcon = config.icon;
            
            const totalPages = Math.ceil(colProjects.length / PAGE_SIZE) || 1;
            const currentPage = pages[colName] || 1;
            const startIndex = (currentPage - 1) * PAGE_SIZE;
            const paginatedProjects = colProjects.slice(startIndex, startIndex + PAGE_SIZE);
            
            return (
              <div key={colName} className="flex flex-col h-full min-w-0 bg-[var(--bg-card)] rounded-2xl border border-[var(--border)] shadow-[var(--shadow-sm)] overflow-hidden">
                {/* Column Header */}
                <div className="flex items-center justify-between p-4 border-b border-[var(--border)] bg-[var(--bg)] bg-opacity-50">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: config.bg, color: config.color }}>
                      <StatusIcon size={16} strokeWidth={2.5} />
                    </div>
                    <span className="text-[15px] font-bold text-[var(--text-primary)] truncate">{colName}</span>
                  </div>
                  <span className="flex items-center justify-center bg-[var(--bg-hover)] text-[var(--text-muted)] text-xs font-bold rounded-full min-w-[24px] h-6 px-2 ml-2 shrink-0 border border-[var(--border-bright)]">
                    {colProjects.length}
                  </span>
                </div>

                {/* Column Body */}
                <div className="flex-1 p-3 overflow-y-auto custom-scrollbar flex flex-col gap-3 bg-[var(--bg-card)]">
                  {paginatedProjects.map((project) => (
                    <div
                      key={project.project_id}
                      onClick={() => handleProjectClick(project.project_id)}
                      className="bg-[var(--bg)] rounded-xl p-4 border border-[var(--border)] hover:border-[var(--accent)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.2)] transition-all duration-200 cursor-pointer group flex flex-col relative"
                    >
                      {/* Accent top border */}
                      <div className="absolute top-0 left-0 right-0 h-1 rounded-t-xl opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: config.color }}></div>
                      
                      <div className="flex justify-between items-start mb-2 mt-1">
                        <h4 className="font-bold text-[var(--text-primary)] text-[15px] group-hover:text-[var(--accent)] transition-colors leading-tight line-clamp-2">
                          {project.project_name}
                        </h4>
                      </div>
                      
                      {project.description && (
                        <p className="text-[13px] text-[var(--text-muted)] line-clamp-2 mb-4 leading-relaxed">
                          {project.description}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.project_key && (
                          <span className="inline-flex items-center px-2 py-1 bg-[var(--bg-card)] text-[var(--text-secondary)] text-[11px] font-bold tracking-wider uppercase rounded border border-[var(--border-bright)]">
                            {project.project_key}
                          </span>
                        )}
                        <span className="inline-flex items-center px-2 py-1 bg-[var(--bg-card)] text-[11px] font-bold tracking-wider uppercase rounded border border-[var(--border-bright)]" style={{ color: config.color }}>
                          {project.priority || 'Medium'}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between mt-auto pt-3 border-t border-[var(--border-bright)]">
                        <div className="flex items-center text-[var(--text-dim)] text-[12px] font-semibold gap-1.5">
                          <Calendar size={13} />
                          <span>{project.end_date ? new Date(project.end_date).toLocaleDateString(undefined, {month: 'short', day: 'numeric'}) : 'No Date'}</span>
                        </div>
                        
                        <div className="flex">
                          {project.project_lead_id ? (
                            <div className="w-7 h-7 rounded-full bg-[var(--bg-hover)] border-2 border-[var(--border-bright)] text-[var(--text-primary)] flex items-center justify-center text-[11px] font-bold shadow-sm" title="Project Lead">
                              <User size={13} />
                            </div>
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-[var(--bg)] border-2 border-[var(--border)] text-[var(--text-dim)] flex items-center justify-center shadow-sm border-dashed">
                              <User size={13} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-[var(--border-bright)]">
                      <button
                        onClick={() => setPages(p => ({ ...p, [colName]: Math.max(1, p[colName] - 1) }))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 text-xs font-bold rounded-md text-[var(--text-primary)] hover:bg-[var(--bg-hover)] disabled:opacity-30 disabled:hover:bg-transparent transition-colors border border-transparent hover:border-[var(--border-bright)]"
                      >
                        Prev
                      </button>
                      <span className="text-[11px] font-bold tracking-widest text-[var(--text-muted)]">
                        {currentPage} / {totalPages}
                      </span>
                      <button
                        onClick={() => setPages(p => ({ ...p, [colName]: Math.min(totalPages, p[colName] + 1) }))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 text-xs font-bold rounded-md text-[var(--text-primary)] hover:bg-[var(--bg-hover)] disabled:opacity-30 disabled:hover:bg-transparent transition-colors border border-transparent hover:border-[var(--border-bright)]"
                      >
                        Next
                      </button>
                    </div>
                  )}

                  {colProjects.length === 0 && (
                    <div className="h-24 flex flex-col items-center justify-center border-2 border-dashed border-[var(--border-bright)] rounded-xl opacity-60 bg-[var(--bg)] m-1">
                      <span className="text-[13px] text-[var(--text-dim)] font-medium">No projects</span>
                    </div>
                  )}

                  {/* Add Project Button - now seamlessly integrated at bottom of column */}
                  <button onClick={() => navigate('/app/projects')} className="mt-1 w-full py-2.5 flex items-center justify-center text-[var(--text-dim)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors text-[13px] font-semibold group border border-transparent hover:border-[var(--border-bright)]">
                    <Plus size={16} className="mr-1.5 group-hover:scale-110 transition-transform" /> Add
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
